/**
 * Car bodies, lofted from cross-sections.
 *
 * THERE ARE NO MODEL FILES, so the shape has to be authored as numbers. The
 * previous car was a single side profile extruded across the width, which gets
 * the silhouette right and nothing else: extrusion cannot taper a greenhouse,
 * cannot flare an arch, and cannot cut a wheel well, so the tyres ended up
 * standing BESIDE a slab rather than sitting inside a car.
 *
 * So a body here is a LOFT. Every car is a list of `BodyStation`s — a
 * longitudinal keyframe carrying six numbers (underbody, beltline, roof, and
 * the half-width at each of those three heights) — which are interpolated with
 * a clamped Catmull-Rom into as many stations as the shape needs, turned into a
 * closed cross-section each, and stitched into a tube. Everything a car reads
 * as comes out of those six numbers:
 *
 *   wRocker < wBelt         the sides tuck under, the way a real door does
 *   wTop    < wBelt         tumblehome: the greenhouse leans in toward the roof
 *   top(x)                  hood -> windscreen rise -> roof -> deck, in one curve
 *   wBelt spiking at an axle  a fender flare
 *   `archInset`             a well cut into the lower body around each wheel
 *
 * The arch is the part worth reading twice. It is not a boolean cut — it is a
 * per-vertex inward displacement over a region SHAPED TO THE TYRE: a circle of
 * the wheel's own radius plus a derived clearance above the axle, opening
 * straight down below it, flat over the inside and ramping to zero across a
 * narrow eyebrow band at its rim. The flat region is the well, the ramp is the
 * eyebrow, and because the beltline flare sits ABOVE the opening the flare
 * survives the cut and overhangs the tyre. The tyre is modelled wide enough to
 * intersect the recessed skin, which is what makes it read as a wheel IN a body
 * rather than a wheel NEXT TO one.
 *
 * Two of those six numbers per station are also load-bearing for STANCE. The
 * underbody between the axles is authored close to the ground — a bright band
 * of arena floor under the whole flank is what makes a car read as lifted — and
 * the beltline over each axle is authored just above the tyre crown, so the
 * fender line follows the wheel instead of floating a wheel-radius above it.
 * Neither touches physics: the hitbox and the suspension rest height are the
 * simulation's, and nothing here is allowed to restate them.
 *
 * CHARACTER LINES. A lofted tube shades as one smooth sheet, which is exactly
 * what a real car is not. Two points of every section are therefore emitted
 * TWICE — once at the belt and once at the shoulder — so the faces above and
 * below each get their own averaged normal and the shading breaks along a hard
 * line running the length of the car. It has to happen in the section generator:
 * a crease added afterwards is a decal, and a decal does not survive the light
 * moving.
 *
 * FRAMES. Stations are authored in the car's own physics frame:
 *
 *     +x = forward (nose)      +y = the car's right      +z = up
 *
 * which is the frame every number in src/physics/constants.ts is quoted in, so
 * an axle at x = 51.25 is written as x = 51.25 and no one has to convert
 * anything by hand. Vertices are emitted straight into the MESH frame that
 * `orientationToQuaternion` in ./convert expects:
 *
 *     -Z = forward (nose)      +Y = up      +X = the car's right
 *
 * by the one-line map (x, y, z) -> (y, z, -x). There is no rotation matrix and
 * no second convention to remember.
 *
 * NOTHING HERE TOUCHES WEBGL. `BufferGeometry` is plain typed arrays until a
 * renderer uploads it, so every function in this file runs in Node and the
 * bodies can be measured in a test.
 *
 * NOTHING HERE RESTATES A PHYSICS NUMBER EITHER. Hitboxes, wheel radii and
 * wheel mounts all come from src/physics/car-config.ts, which owns them; this
 * file adds only ART — station tables, tyre widths, the cabin and roof ranges,
 * the wing. The two are joined at `CAR_BODY_SPECS`, and `CarBodyId` is a
 * literal subset of that module's `CarBody`, so a body renamed over there
 * fails to compile over here rather than silently falling back.
 */

import { BufferAttribute, BufferGeometry } from 'three';
import { CAR_SPAWN_REST_Z, SUSPENSION } from '../physics/constants';
import { carConfigFor, type CarBody, type CarConfig } from '../physics/car-config';

// ---------------------------------------------------------------------------
// Small pure maths
// ---------------------------------------------------------------------------

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Hermite ease, clamped. Used for section curvature, never for physics. */
export const smoothstep = (t: number): number => {
  const k = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return k * k * (3 - 2 * k);
};

/**
 * Catmull-Rom through four samples, CLAMPED to the span it is interpolating.
 *
 * The clamp is not cosmetic. A plain Catmull-Rom overshoots wherever the
 * keyframes change slope hard — which for a car is the windscreen base and the
 * rear axle flare, i.e. exactly the two places a bulge would be obvious. The
 * clamp costs the curve nothing anywhere else.
 */
export const catmullClamped = (
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number
): number => {
  const t2 = t * t;
  const t3 = t2 * t;
  const v =
    0.5 *
    (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
  const lo = Math.min(p1, p2);
  const hi = Math.max(p1, p2);
  return v < lo ? lo : v > hi ? hi : v;
};

// ---------------------------------------------------------------------------
// Body specification
// ---------------------------------------------------------------------------

/**
 * The bodies there is ART for — a subset of `CarBody` in
 * src/physics/car-config.ts, which knows about all seven of RocketSim's. It is
 * written as an `Extract` rather than a fresh union so that adding a station
 * table for, say, the Breakout is a one-word change here and a compile error
 * if the physics side ever spells a body differently.
 */
export type CarBodyId = Extract<CarBody, 'octane' | 'dominus'>;

/** Selection order, and the order the menu cycles in. */
export const CAR_BODY_IDS: readonly CarBodyId[] = ['octane', 'dominus'];

/** One longitudinal keyframe of a body. All values uu, car frame. */
export interface BodyStation {
  /** Forward from the car origin. Nose positive; stations run nose -> tail. */
  x: number;
  /** Underbody height. Below this is floor, and floor is never painted. */
  bottom: number;
  /** Beltline: the widest height of the section, where the greenhouse starts. */
  belt: number;
  /** Crown height — hood, roof or rear deck depending where along the car. */
  top: number;
  /** Half-width at the rocker (the bottom of the door). */
  wRocker: number;
  /** Half-width at the belt. The widest point of the section. */
  wBelt: number;
  /** Half-width at the shoulder just under the crown. Less than `wBelt`. */
  wTop: number;
}

/**
 * One axle's worth of wheel. `radius` and `mount` are lifted straight off the
 * physics config; only `width` is art.
 */
export interface WheelSpec {
  radius: number;
  /** Suspension attachment in the car frame; y is the +y (right) side. */
  mount: { readonly x: number; readonly y: number; readonly z: number };
  /** Tyre width across the axle, uu. Art, not physics. */
  width: number;
}

export interface CarBodySpec {
  id: CarBodyId;
  label: string;
  /** One-line character note; the menu shows it under the row. */
  note: string;
  /** The physics preset this body is drawn around. */
  config: CarConfig;
  /** Collision extents (full, not half), unpacked from `config`. */
  hitbox: { readonly length: number; readonly width: number; readonly height: number };
  /** Hitbox centre offset from the car origin, car frame. */
  hitboxOffset: { readonly x: number; readonly y: number; readonly z: number };
  front: WheelSpec;
  back: WheelSpec;
  /**
   * How far OUTBOARD of the suspension mount the visible wheel is planted.
   *
   * RocketSim's mount y is a raycast attachment point, not an art position, and
   * the art it is attached to sits a hair further out — enough that the tyre's
   * outer face lands under the fender flare instead of half a unit inside it.
   * One number per car, so the gap between physics and art is stated rather
   * than smeared through the builder.
   */
  wheelOutboard: number;
  stations: readonly BodyStation[];
  /** x range the glass canopy covers. */
  cabin: { readonly front: number; readonly back: number };
  /** x range the painted roof panel covers; sits inside `cabin`. */
  roof: { readonly front: number; readonly back: number };
  /** How far the lower body is pulled in at the middle of a wheel well. */
  archDepth: number;
  /** Rear wing: a raised blade (Octane) or a deck-height lip (Dominus). */
  wing: { readonly x: number; readonly z: number; readonly chord: number; readonly halfWidth: number; readonly stand: number };
  /** Exhaust tips per side. */
  exhausts: number;
}

/**
 * Height of the car origin above the floor at rest.
 *
 * Every wheel is planted at `radius - CAR_ORIGIN_HEIGHT` so its contact patch
 * lands on z = 0 in world space. That is a RENDER fact and it stays a flat 17
 * for both bodies because the SIMULATION still uses one suspension model for
 * every car (RocketSim does the same). `WheelSpec.mount.z` is not what places
 * the wheel vertically and must not be used to.
 */
export const CAR_ORIGIN_HEIGHT = CAR_SPAWN_REST_Z;

/** Wheel centre height in the car frame, from its radius. */
export const wheelCentreZ = (radius: number): number => radius - CAR_ORIGIN_HEIGHT;

// ---------------------------------------------------------------------------
// The two cars
// ---------------------------------------------------------------------------

/**
 * Octane: the compact hot-rod. Short overhangs, an upright cabin sitting well
 * forward, a tall roof at z = 39 (the hitbox tops out at 40.08), and rear
 * arches flared 5 uu past the front ones to carry the bigger back wheels.
 *
 * The sill runs at z = -10 the whole way between the axles — 7 uu off the floor
 * once the origin is lifted to `CAR_ORIGIN_HEIGHT` — and the beltline is
 * authored DOWN toward the tyre crowns at both axles (12.5 over a crown at 8 in
 * front, 17 over a crown at 13 behind) so the fender line sits four uu above the
 * rubber instead of a wheel radius above it. The nose bottom then climbs hard
 * over the last 15 uu: the old table carried the underbody flat out to x = 66
 * and let the section balloon below the lamp line, which is what read as a
 * pickup snout on the front three-quarter.
 *
 * SEVEN uu and not less, because the renderer draws the chassis at the
 * SIMULATION's height and that height moves. An ordinary jump lands at roughly
 * 300 uu/s of closing speed, and `SUSPENSION.dampingCompression` is a
 * per-unit-mass 25 * v, so it arrests that in v^2 / 2a ~ 6 uu of extra travel:
 * a sill lower than about seven would scrape the pitch every time the car came
 * down. Nothing here changes the ride height the physics uses — the hitbox and
 * `CAR_SPAWN_REST_Z` are untouched — only how much bodywork is drawn below it.
 */
const OCTANE_STATIONS: readonly BodyStation[] = [
  { x: 74, bottom: 7, belt: 12, top: 15, wRocker: 14, wBelt: 24, wTop: 18 },
  { x: 71, bottom: 4, belt: 12.5, top: 18, wRocker: 23, wBelt: 31, wTop: 26 },
  { x: 66, bottom: -1, belt: 13, top: 20, wRocker: 28, wBelt: 34, wTop: 30 },
  { x: 58, bottom: -7, belt: 13, top: 21, wRocker: 31, wBelt: 37.5, wTop: 31 },
  { x: 51, bottom: -10, belt: 12.5, top: 21, wRocker: 31, wBelt: 38.5, wTop: 32 },
  { x: 43, bottom: -10, belt: 13, top: 21, wRocker: 31, wBelt: 35, wTop: 31 },
  { x: 34, bottom: -10, belt: 14, top: 21, wRocker: 32, wBelt: 34, wTop: 31 },
  { x: 26, bottom: -10, belt: 15, top: 22, wRocker: 32, wBelt: 34, wTop: 31 },
  { x: 20, bottom: -10, belt: 16, top: 27, wRocker: 32, wBelt: 34, wTop: 29 },
  { x: 13, bottom: -10, belt: 16.5, top: 34, wRocker: 32, wBelt: 34, wTop: 26 },
  { x: 6, bottom: -10, belt: 17, top: 39, wRocker: 32, wBelt: 34, wTop: 24 },
  { x: -6, bottom: -10, belt: 17, top: 39, wRocker: 32, wBelt: 34, wTop: 24 },
  { x: -18, bottom: -10, belt: 17, top: 38, wRocker: 32, wBelt: 35, wTop: 25 },
  { x: -26, bottom: -10, belt: 17, top: 33, wRocker: 32, wBelt: 37, wTop: 29 },
  { x: -34, bottom: -10, belt: 17, top: 27, wRocker: 32, wBelt: 44, wTop: 34 },
  { x: -42, bottom: -8, belt: 17, top: 26, wRocker: 31, wBelt: 44, wTop: 34 },
  // The tail stays wide almost to the tip. A boat-tail looks soft and, more
  // practically, leaves the rear lamps nothing flat to sit on.
  { x: -50, bottom: -4, belt: 16.5, top: 25, wRocker: 30, wBelt: 41, wTop: 33 },
  { x: -57, bottom: 1, belt: 16, top: 24, wRocker: 28, wBelt: 36, wTop: 30 },
  { x: -60, bottom: 5, belt: 13, top: 21, wRocker: 21, wBelt: 26, wTop: 20 },
];

/**
 * Dominus: the long flat muscle car. 9 uu longer than the Octane, roof 7 uu
 * lower, a hood that runs half the car before the screen even starts, a chopped
 * cabin pushed right back and a fastback that falls into the deck instead of
 * dropping off it. Wider track, smaller wheels.
 *
 * Sill at z = -10.5, half a unit lower than the Octane's, which is the whole
 * point of the body. The rear belt is the one number here that is set by an
 * INVARIANT rather than by eye: the Dominus rear tyre crowns at z = 10 and its
 * arch opening reaches 12.6 (see `archClearance`), so a belt below ~13.5 would
 * have the well eat its own eyebrow. 14 keeps a unit and a half of flare above
 * it.
 */
const DOMINUS_STATIONS: readonly BodyStation[] = [
  { x: 80, bottom: 4, belt: 9, top: 11, wRocker: 16, wBelt: 25, wTop: 18 },
  { x: 76, bottom: 1, belt: 9.5, top: 13, wRocker: 25, wBelt: 32, wTop: 27 },
  { x: 70, bottom: -4, belt: 10, top: 15, wRocker: 31, wBelt: 36, wTop: 31 },
  { x: 62, bottom: -8, belt: 11, top: 16, wRocker: 34, wBelt: 41.5, wTop: 34 },
  { x: 50, bottom: -10.5, belt: 11.5, top: 16, wRocker: 34, wBelt: 43, wTop: 35 },
  { x: 40, bottom: -10.5, belt: 12, top: 16, wRocker: 34, wBelt: 39, wTop: 34 },
  { x: 30, bottom: -10.5, belt: 12.5, top: 16, wRocker: 34, wBelt: 38, wTop: 34 },
  { x: 20, bottom: -10.5, belt: 13, top: 17, wRocker: 34, wBelt: 38, wTop: 34 },
  { x: 12, bottom: -10.5, belt: 13, top: 22, wRocker: 34, wBelt: 38, wTop: 31 },
  { x: 2, bottom: -10.5, belt: 13, top: 29, wRocker: 34, wBelt: 38, wTop: 27 },
  { x: -8, bottom: -10.5, belt: 13, top: 32, wRocker: 34, wBelt: 38, wTop: 25 },
  { x: -18, bottom: -10.5, belt: 13.5, top: 31, wRocker: 34, wBelt: 39, wTop: 26 },
  { x: -26, bottom: -10.5, belt: 14, top: 27, wRocker: 34, wBelt: 43, wTop: 30 },
  { x: -35, bottom: -10.5, belt: 14, top: 22, wRocker: 34, wBelt: 46, wTop: 36 },
  { x: -44, bottom: -9, belt: 14, top: 20, wRocker: 33, wBelt: 46, wTop: 36 },
  { x: -52, bottom: -5, belt: 13, top: 19, wRocker: 31, wBelt: 43, wTop: 34 },
  { x: -59, bottom: 0, belt: 11, top: 18, wRocker: 29, wBelt: 37, wTop: 31 },
  { x: -63, bottom: 4, belt: 9, top: 15, wRocker: 21, wBelt: 26, wTop: 20 },
];

/** Everything about a body that is ART. The physics half comes from its config. */
type CarBodyArt = Omit<CarBodySpec, 'id' | 'config' | 'hitbox' | 'hitboxOffset' | 'front' | 'back'> & {
  frontTyreWidth: number;
  backTyreWidth: number;
};

/**
 * Join one body's art to its physics preset. Nothing in the result is typed
 * twice: the hitbox and both axles are unpacked from `carConfigFor`, and only
 * the tyre widths — which are art, RocketSim has no such number — come from
 * this side.
 */
const bodySpec = (id: CarBodyId, art: CarBodyArt): CarBodySpec => {
  const config = carConfigFor(id);
  const { frontTyreWidth, backTyreWidth, ...rest } = art;
  return {
    id,
    config,
    hitbox: {
      length: config.hitboxSize.x,
      width: config.hitboxSize.y,
      height: config.hitboxSize.z,
    },
    hitboxOffset: config.hitboxOffset,
    front: {
      radius: config.frontWheels.radius,
      mount: config.frontWheels.connectionOffset,
      width: frontTyreWidth,
    },
    back: {
      radius: config.backWheels.radius,
      mount: config.backWheels.connectionOffset,
      width: backTyreWidth,
    },
    ...rest,
  };
};

export const CAR_BODY_SPECS: Readonly<Record<CarBodyId, CarBodySpec>> = {
  octane: bodySpec('octane', {
    label: 'Octane',
    note: 'Short, tall hot-rod. Big rear wheels, upright cabin, stubby nose.',
    frontTyreWidth: 19,
    backTyreWidth: 22,
    wheelOutboard: 1,
    stations: OCTANE_STATIONS,
    cabin: { front: 22, back: -28 },
    roof: { front: 9, back: -21 },
    // Six, not the eight it was. The tyre only has to overlap the recessed skin
    // to read as being IN the body — it clears it by 11 uu at six — and every
    // unit past that is amplitude on the step the eyebrow ramp has to smooth.
    archDepth: 6,
    wing: { x: -46, z: 32, chord: 19, halfWidth: 33, stand: 7 },
    exhausts: 1,
  }),
  dominus: bodySpec('dominus', {
    label: 'Dominus',
    note: 'Long flat muscle car. Long hood, chopped roof, wide track.',
    frontTyreWidth: 18,
    backTyreWidth: 20,
    wheelOutboard: 0.5,
    stations: DOMINUS_STATIONS,
    cabin: { front: 14, back: -30 },
    roof: { front: 3, back: -20 },
    archDepth: 6,
    // A ducktail rather than a blade: sits ON the deck, which is what keeps the
    // Dominus silhouette flat when the two cars are side by side.
    wing: { x: -44, z: 21.5, chord: 14, halfWidth: 38, stand: 2 },
    exhausts: 2,
  }),
};

export const carBodySpec = (id: CarBodyId): CarBodySpec =>
  CAR_BODY_SPECS[id] ?? CAR_BODY_SPECS.octane;

/** Lateral centre of the visible wheel on the +y side of an axle. */
export const wheelCentreY = (spec: CarBodySpec, front: boolean): number =>
  (front ? spec.front.mount.y : spec.back.mount.y) + spec.wheelOutboard;

// ---------------------------------------------------------------------------
// Longitudinal sampling
// ---------------------------------------------------------------------------

type StationKey = keyof Omit<BodyStation, 'x'>;
const STATION_KEYS: readonly StationKey[] = ['bottom', 'belt', 'top', 'wRocker', 'wBelt', 'wTop'];

/**
 * The station at an arbitrary x, Catmull-Rom through the keyframes. Beyond
 * either end it clamps rather than extrapolating — a body that grows past its
 * own nose is a body with no bounds.
 */
export const sampleStation = (stations: readonly BodyStation[], x: number): BodyStation => {
  const n = stations.length;
  if (n === 0) throw new Error('car body has no stations');
  if (x >= stations[0].x) return { ...stations[0], x };
  if (x <= stations[n - 1].x) return { ...stations[n - 1], x };

  let i = 0;
  while (i < n - 2 && stations[i + 1].x > x) i++;
  const p1 = stations[i];
  const p2 = stations[i + 1];
  const p0 = stations[i - 1] ?? p1;
  const p3 = stations[i + 2] ?? p2;
  const span = p1.x - p2.x;
  const t = span > 1e-9 ? (p1.x - x) / span : 0;

  const out = { x } as BodyStation;
  for (const key of STATION_KEYS) {
    out[key] = catmullClamped(p0[key], p1[key], p2[key], p3[key], t);
  }
  return out;
};

/**
 * Where to cut the loft, nose to tail. Every keyframe is kept exactly — a
 * keyframe that only nearly lands is a keyframe that does not do what it says —
 * and the gaps are filled to `step`, tightened to `archStep` over each wheel so
 * the arch curve has something to be drawn on.
 */
export const stationXs = (spec: CarBodySpec, step = 5.5, archStep = ARCH_STEP): number[] => {
  const stations = spec.stations;
  // Exactly the span the arch touches, so the eyebrow ramp — 3.2 uu wide — gets
  // rings inside it rather than being jumped over by one long quad.
  const archRanges = ([true, false] as const).map((front) => {
    const reach = archHalfLength(spec, front);
    const mount = front ? spec.front.mount.x : spec.back.mount.x;
    return { lo: mount - reach, hi: mount + reach };
  });
  const nearArch = (x: number): boolean => archRanges.some((r) => x >= r.lo && x <= r.hi);

  const xs: number[] = [];
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i].x;
    const b = stations[i + 1].x;
    const local = nearArch(a) || nearArch(b) ? archStep : step;
    const count = Math.max(1, Math.ceil((a - b) / local));
    for (let k = 0; k < count; k++) xs.push(lerp(a, b, k / count));
  }
  xs.push(stations[stations.length - 1].x);
  return xs;
};

// ---------------------------------------------------------------------------
// Wheel arches
// ---------------------------------------------------------------------------

/**
 * How much longer the arch opening is than it is tall. Real arches are barely
 * oval; the old 1.5 : 1.18 ellipse opened a hole half a wheel longer than the
 * tyre and the daylight fore and aft of the rubber read as ride height.
 */
const ARCH_LONG = 1.12;

/**
 * Ring spacing over an arch, uu — and therefore the finest detail the loft can
 * draw there. `stationXs` cuts at this pitch over the wheels.
 */
const ARCH_STEP = 2;
/**
 * ...and the eyebrow gets this many of those rings across it, whatever the
 * derived clearance asks for. A ramp one ring wide steps the skin the full
 * depth of the well between two adjacent sections, and where the arch's rim
 * runs diagonally across the rings — the trailing edge, down into the sill —
 * that steps as a sawtooth instead of a curve. A ring and a half is the
 * cheapest width at which it reads as a fillet.
 */
const ARCH_EYEBROW_RINGS = 1.6;

/**
 * Clearance between the tyre crown and the top of its arch opening, uu.
 *
 * DERIVED, not chosen — and the derivation is the reason the openings are as
 * tight as they now are.
 *
 * A wheel that punches through its own bodywork looks far worse than a gap, so
 * the opening has to clear whatever the wheel does relative to the shell. What
 * it does is set by the suspension, and the suspension model is RocketSim's
 * (../physics/suspension): a spring whose zero-force length is
 * `suspensionRestLength - SUSPENSION.maxTravel`, sitting at rest with the car's
 * origin at `CAR_ORIGIN_HEIGHT` and the contact patch on the floor. So at rest
 * that spring is already compressed by
 *
 *     springRestLength - (CAR_ORIGIN_HEIGHT + mount.z - radius)
 *
 * which is 1.5 / 2.3 uu on the Octane's two axles and 1.2 / 2.6 on the
 * Dominus's — the distance each wheel has ALREADY travelled up into the body to
 * hold the car at the rest height the renderer draws it at. Equivalently, it is
 * how far each wheel drops away from the shell the moment the car leaves the
 * ground and its springs extend. That is the full excursion between the two
 * states this renderer ever shows, so it is exactly the clearance the arch owes
 * the tyre, and every extra unit past it is a gap bought for nothing.
 *
 * It is honest about what it does NOT cover: the wheels are drawn rigid to the
 * chassis (`wheelCentreZ`), so a hard landing lowers body and wheel together and
 * the arch never sees the further ~10 uu of bump travel the springs have. Wire
 * the visible wheels to `WheelContact.suspensionLength` one day and the eyebrows
 * will kiss a tyre on impacts past about 6 g — at which point the correct fix is
 * to clamp the visual travel, not to reopen the arches.
 *
 * It also comes out RIGHT WAY ROUND by itself: the rear springs carry more of
 * the car, sit more compressed at rest, and get the roomier arches.
 *
 * And it is the EYEBROW WIDTH as well as the clearance, which is what makes the
 * arch tight rather than merely small: the well runs at full depth out to the
 * tyre's own radius — where the tyre is in front of it and it cannot be seen —
 * and ramps back to untouched paint over exactly this distance. Nothing between
 * the rubber and the sheet metal is wider than the suspension needs.
 */
export const archClearance = (spec: CarBodySpec, front: boolean): number => {
  const pair = front ? spec.config.frontWheels : spec.config.backWheels;
  const springRestLength = pair.suspensionRestLength - SUSPENSION.maxTravel;
  const atRest = CAR_ORIGIN_HEIGHT + pair.connectionOffset.z - pair.radius;
  return springRestLength - atRest;
};

/** Width of one axle's eyebrow ramp: the clearance, or what the loft can draw. */
const archEyebrow = (spec: CarBodySpec, front: boolean): number =>
  Math.max(archClearance(spec, front), ARCH_STEP * ARCH_EYEBROW_RINGS);

/** Radius of one axle's arch opening: the tyre plus its eyebrow. */
export const archOpeningRadius = (spec: CarBodySpec, front: boolean): number =>
  (front ? spec.front.radius : spec.back.radius) + archEyebrow(spec, front);

/** How far fore and aft of the axle the arch reaches, eyebrow included. */
export const archHalfLength = (spec: CarBodySpec, front: boolean): number =>
  archOpeningRadius(spec, front) * ARCH_LONG;

/**
 * How far the skin is pulled inward at (x, z) by one wheel well, in uu.
 *
 * The opening follows the TYRE: a circle of the wheel's OWN radius about the
 * axle above the wheel centre, stretched by `ARCH_LONG` along the car, opening
 * straight down below the centre — a real arch does not close under its own
 * tyre, and the flank below the axle is where the well has to keep running for
 * the skirt to have something to end against. Full depth inside that circle,
 * where the tyre itself hides the recess, ramping back to untouched paint over
 * `clearance` beyond it. The beltline flare sits above the whole opening, so it
 * never gets cut and ends up overhanging the tyre.
 */
export const archInset = (
  x: number,
  z: number,
  wheelX: number,
  wheelZ: number,
  radius: number,
  depth: number,
  eyebrow: number
): number => {
  const dx = (x - wheelX) / ARCH_LONG;
  const dz = z - wheelZ;
  const d = dz > 0 ? Math.hypot(dx, dz) : Math.abs(dx);
  const outer = radius + eyebrow;
  if (d >= outer) return 0;
  return depth * smoothstep((outer - d) / eyebrow);
};

/** Combined inset from both axles. They never overlap on any body here. */
export const archInsetFor = (spec: CarBodySpec, x: number, z: number): number => {
  const front = archInset(
    x,
    z,
    spec.front.mount.x,
    wheelCentreZ(spec.front.radius),
    spec.front.radius,
    spec.archDepth,
    archEyebrow(spec, true)
  );
  const back = archInset(
    x,
    z,
    spec.back.mount.x,
    wheelCentreZ(spec.back.radius),
    spec.back.radius,
    spec.archDepth,
    archEyebrow(spec, false)
  );
  return Math.max(front, back);
};

// ---------------------------------------------------------------------------
// Cross-sections
// ---------------------------------------------------------------------------

/**
 * Samples from the rocker up to the belt. The arch is drawn on these, so this
 * is what resolves the eyebrow vertically, the way `ARCH_STEP` does along the
 * car: ten over a ~23 uu flank is close enough to the same two uu a step.
 */
const SIDE_SAMPLES = 10;
/** Samples from the belt up to the shoulder. */
const UPPER_SAMPLES = 4;
/** Extra samples rounding the shoulder over into the crown. */
const CROWN_SAMPLES = 2;
/** How much of the crown height is spent on that rounding. */
const CROWN_DROP = 2.2;

/** A cross-section point: (y = right, z = up), in the car frame. */
export type SectionPoint = readonly [number, number];

/**
 * Where each run of points starts inside a closed section, so a caller can name
 * a band of the body ("the greenhouse", "the crown") by INDEX rather than by
 * height.
 *
 * By index and not by height is the whole point. Filtering a section on `z`
 * gives a different number of points at every station — the belt moves, the
 * crown moves — and a loft whose rings have different lengths cannot be
 * stitched at all. The layout below is identical at every station by
 * construction, so any index range is a well-defined strip down the whole car.
 *
 *     0                 underbody centreline
 *     floor .. crown    the RIGHT half, bottom to top
 *     top               crown centreline
 *     top+1 .. count-1  the LEFT half, mirrored, top to bottom
 *
 * `shoulder` and `hood` are the two CHARACTER LINES, and each is a pair of
 * coincident points rather than one: `shoulder` is the belt emitted twice and
 * `hood` is the top of the greenhouse emitted twice. The duplicate carries no
 * area — the quad between the two is degenerate — so all it costs is a vertex
 * per station, and what it buys is that `computeVertexNormals` averages the
 * faces below the line into one copy and the faces above it into the other.
 * That is a tangent break, i.e. a crease the light actually catches, which is
 * the only way to get one on a body that is otherwise a single smooth loft.
 */
export const SECTION_INDEX = {
  floor: 1,
  chamfer: 2,
  sideStart: 3,
  /** The beltline, doubled: the shoulder line down the whole flank. */
  shoulder: 3 + SIDE_SAMPLES - 1,
  upperStart: 3 + SIDE_SAMPLES + 1,
  /** The top of the greenhouse, doubled: the hood / roof edge. */
  hood: 3 + SIDE_SAMPLES + UPPER_SAMPLES,
  crownStart: 3 + SIDE_SAMPLES + UPPER_SAMPLES + 2,
  top: 3 + SIDE_SAMPLES + UPPER_SAMPLES + CROWN_SAMPLES + 2,
} as const;

/** Points in one closed section. Both halves plus the two centreline points. */
export const SECTION_POINTS = SECTION_INDEX.top * 2;

/**
 * Half a cross-section: from the underbody centreline out and up to the crown
 * centreline, EXCLUDING both centreline points. Mirroring this is what closes
 * the loop, so a car can never come out asymmetric.
 */
export const halfSection = (station: BodyStation, inset: (z: number) => number): SectionPoint[] => {
  const { bottom, belt, top, wRocker, wBelt, wTop } = station;
  const crownTop = Math.max(bottom + 0.5, top - CROWN_DROP);
  const pts: SectionPoint[] = [];

  /** Never let a well eat the whole section; a pinched-to-zero side self-folds. */
  const cut = (w: number, z: number): number => Math.max(w * 0.35, w - inset(z));

  // Floor, then the rocker chamfer. The floor centreline stays uncut — the
  // underbody is a flat plate and a well carved through it would show from
  // below — but the chamfer follows the well, or the skirt would step out
  // under the tyre.
  pts.push([wRocker * 0.55, bottom]);
  pts.push([cut(wRocker * 0.94, bottom + 0.7), bottom + 0.7]);

  // Rocker -> belt. `smoothstep` on the width and linear on the height gives
  // the section a tucked-under lower half rather than a straight taper, and
  // lands the last sample exactly ON `wBelt` — the widest point of the car has
  // to be a vertex, or the shoulder line has nothing to break along.
  for (let i = 1; i <= SIDE_SAMPLES; i++) {
    const t = i / SIDE_SAMPLES;
    const z = lerp(bottom + 0.7, belt, t);
    const w = lerp(wRocker, wBelt, smoothstep(t));
    pts.push([cut(w, z), z]);
  }

  // The shoulder line: the belt point again. The side run arrives at it
  // vertically (smoothstep flattens out) and the greenhouse leaves it leaning
  // inward, so the two copies end up with genuinely different normals.
  pts.push(pts[pts.length - 1]);

  // Belt -> shoulder: the greenhouse, leaning in.
  for (let i = 1; i <= UPPER_SAMPLES; i++) {
    const t = i / UPPER_SAMPLES;
    const z = lerp(belt, crownTop, t);
    const w = lerp(wBelt, wTop, Math.pow(t, 0.8));
    pts.push([w, z]);
  }

  // The hood line: the top of the greenhouse again, for the same reason. Over
  // the nose this is the crease along the edge of the hood, over the cabin the
  // roof rail, over the tail the edge of the deck — one line, whole car.
  pts.push(pts[pts.length - 1]);

  // Shoulder -> crown, as a quarter ellipse so the roof has a radius on it.
  for (let i = 1; i <= CROWN_SAMPLES; i++) {
    const a = ((i / (CROWN_SAMPLES + 1)) * Math.PI) / 2;
    pts.push([wTop * Math.cos(a), crownTop + (top - crownTop) * Math.sin(a)]);
  }

  return pts;
};

/**
 * The closed cross-section, counter-clockwise in the (y, z) plane starting at
 * the underbody centreline: centre-bottom, up the RIGHT side, centre-top, down
 * the left. The winding is load-bearing — ./buildLoft assumes it when it decides
 * which way a face points.
 */
export const sectionOutline = (spec: CarBodySpec, station: BodyStation): SectionPoint[] => {
  const half = halfSection(station, (z) => archInsetFor(spec, station.x, z));
  const loop: SectionPoint[] = [[0, station.bottom]];
  for (const p of half) loop.push(p);
  loop.push([0, station.top]);
  for (let i = half.length - 1; i >= 0; i--) loop.push([-half[i][0], half[i][1]]);
  return loop;
};

/**
 * Half-width of the painted shell at (x, z), by interpolating the section that
 * `sectionOutline` would build there.
 *
 * This is how the lamps, mirrors and side accents get planted ON the body
 * instead of near it: hard-coding a y for a headlight works until the station
 * table moves by a unit, and then the lamp is floating and nobody notices until
 * a screenshot. Above the crown or below the floor it returns the nearest
 * width rather than zero, which keeps callers from having to bounds-check.
 */
export const bodyHalfWidthAt = (spec: CarBodySpec, x: number, z: number): number => {
  const station = sampleStation(spec.stations, x);
  const half = halfSection(station, (zz) => archInsetFor(spec, x, zz));
  if (z <= half[0][1]) return half[0][0];
  for (let i = 1; i < half.length; i++) {
    if (z <= half[i][1]) {
      const a = half[i - 1];
      const b = half[i];
      const span = b[1] - a[1];
      const t = span > 1e-9 ? (z - a[1]) / span : 0;
      return lerp(a[0], b[0], t);
    }
  }
  return half[half.length - 1][0];
};

// ---------------------------------------------------------------------------
// Lofting
// ---------------------------------------------------------------------------

/** Which material group a face belongs to. Index into the mesh's material array. */
export const BODY_GROUP_PAINT = 0;
export const BODY_GROUP_UNDER = 1;

interface LoftOptions {
  /** Cap the nose ring and the tail ring. Bands (canopy, roof) do not. */
  caps: boolean;
  /** Split the underbody into its own material group. */
  splitUnderbody: boolean;
}

/**
 * Stitch rings of equal length into a tube.
 *
 * Rings arrive nose-first, so consecutive rings advance along +Z in the mesh
 * frame, and each ring is counter-clockwise in mesh XY. Winding a quad
 * (r0[j], r0[j+1], r1[j+1], r1[j]) then produces an OUTWARD normal — worked
 * once, here, and pinned by a test, because a body with inverted normals still
 * renders and just looks subtly, unexplainably wrong.
 */
const buildLoft = (
  rings: readonly SectionPoint[][],
  xs: readonly number[],
  bottoms: readonly number[],
  options: LoftOptions
): BufferGeometry => {
  const ringCount = rings.length;
  const n = rings[0].length;
  const vertexCount = ringCount * n + (options.caps ? 2 : 0);

  const positions = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const noseX = xs[0];
  const tailX = xs[xs.length - 1];
  const span = Math.max(1e-6, noseX - tailX);

  for (let i = 0; i < ringCount; i++) {
    const ring = rings[i];
    for (let j = 0; j < n; j++) {
      const o = (i * n + j) * 3;
      // Car frame (x fwd, y right, z up) -> mesh frame (+X right, +Y up, -Z fwd).
      positions[o] = ring[j][0];
      positions[o + 1] = ring[j][1];
      positions[o + 2] = -xs[i];
      const u = (i * n + j) * 2;
      uvs[u] = j / n;
      uvs[u + 1] = (noseX - xs[i]) / span;
    }
  }

  const paint: number[] = [];
  const under: number[] = [];
  for (let i = 0; i < ringCount - 1; i++) {
    const a = i * n;
    const b = (i + 1) * n;
    // The underbody threshold is per-station, because `bottom` rises at both
    // ends of the car and a flat cut-off would paint the nose floor.
    const floorA = bottoms[i] + 1.2;
    const floorB = bottoms[i + 1] + 1.2;
    for (let j = 0; j < n; j++) {
      const k = (j + 1) % n;
      const isFloor =
        options.splitUnderbody &&
        rings[i][j][1] <= floorA &&
        rings[i][k][1] <= floorA &&
        rings[i + 1][j][1] <= floorB &&
        rings[i + 1][k][1] <= floorB;
      const target = isFloor ? under : paint;
      target.push(a + j, a + k, b + k, a + j, b + k, b + j);
    }
  }

  if (options.caps) {
    const nose = ringCount * n;
    const tail = nose + 1;
    const write = (index: number, ring: readonly SectionPoint[], x: number): void => {
      let y = 0;
      let z = 0;
      for (const p of ring) {
        y += p[0];
        z += p[1];
      }
      positions[index * 3] = y / ring.length;
      positions[index * 3 + 1] = z / ring.length;
      positions[index * 3 + 2] = -x;
      uvs[index * 2] = 0.5;
      uvs[index * 2 + 1] = x === noseX ? 0 : 1;
    };
    write(nose, rings[0], noseX);
    write(tail, rings[ringCount - 1], tailX);
    const last = (ringCount - 1) * n;
    for (let j = 0; j < n; j++) {
      const k = (j + 1) % n;
      // Reversed at the nose: the fan there has to face -Z, the tail +Z.
      paint.push(nose, k, j);
      paint.push(tail, last + j, last + k);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex([...paint, ...under]);
  geometry.addGroup(0, paint.length, BODY_GROUP_PAINT);
  if (under.length > 0) geometry.addGroup(paint.length, under.length, BODY_GROUP_UNDER);
  geometry.computeVertexNormals();
  return geometry;
};

/**
 * The painted shell: two material groups, paint over an unpainted floor.
 */
export const buildBodyGeometry = (spec: CarBodySpec): BufferGeometry => {
  const xs = stationXs(spec);
  const stations = xs.map((x) => sampleStation(spec.stations, x));
  const rings = stations.map((s) => sectionOutline(spec, s));
  const geometry = buildLoft(rings, xs, stations.map((s) => s.bottom), {
    caps: true,
    splitUnderbody: true,
  });
  geometry.name = `${spec.id}-body`;
  return geometry;
};

/**
 * A shell laid ON the body over part of its length: the glass canopy and the
 * painted roof panel are both this, and both have to follow the body's own
 * curvature exactly or they float.
 *
 * `fromIndex` names the band as a symmetric index range out of `SECTION_INDEX`
 * and `offset` pushes it outward along the section's 2D normal, which is why a
 * canopy sits proud of the paint by a constant film thickness instead of
 * z-fighting with it in patches.
 */
export const buildBandGeometry = (
  spec: CarBodySpec,
  range: { front: number; back: number },
  fromIndex: number,
  offset: number,
  thickness = 0.9
): BufferGeometry => {
  const xs = stationXs(spec).filter((x) => x <= range.front && x >= range.back);
  if (xs.length < 2) throw new Error(`band range is empty for ${spec.id}`);
  const toIndex = SECTION_INDEX.top * 2 - fromIndex;
  if (fromIndex < 1 || toIndex <= fromIndex) throw new Error(`band index ${fromIndex} is empty`);

  const rings = xs.map((x) => {
    const station = sampleStation(spec.stations, x);
    const kept = sectionOutline(spec, station).slice(fromIndex, toIndex + 1);

    const shift = (d: number): SectionPoint[] =>
      kept.map((p, i): SectionPoint => {
        const prev = kept[Math.max(0, i - 1)];
        const next = kept[Math.min(kept.length - 1, i + 1)];
        const ty = next[0] - prev[0];
        const tz = next[1] - prev[1];
        const len = Math.hypot(ty, tz) || 1;
        // The outline runs anticlockwise, so (tz, -ty) points outward.
        return [p[0] + (tz / len) * d, p[1] - (ty / len) * d];
      });

    // A band is an open strip, but a zero-thickness strip z-fights with itself
    // the moment it is drawn from both sides. So it is closed into a very thin
    // tube: outer face out, inner face back along the arc a hair below the
    // body's own skin. The two joins at the ends of the arc are degenerate
    // quads, which cost two triangles and are never visible.
    return [...shift(offset), ...shift(offset - thickness).reverse()];
  });

  const geometry = buildLoft(rings, xs, xs.map(() => -Infinity), {
    caps: false,
    splitUnderbody: false,
  });
  geometry.name = `${spec.id}-band`;
  return geometry;
};

/**
 * Concatenate geometries that share one material into a single draw.
 *
 * A wheel is a barrel, a lip, six spokes and a hub, and four of them per car
 * times six cars is 240 draws for parts that never move relative to each other.
 * Baking the transforms into the geometry and merging costs nothing and takes
 * that to 24.
 *
 * Positions, normals and uvs only, and MATERIAL GROUPS ARE DROPPED. Three's own
 * primitives group their faces (a box has six), so refusing grouped input would
 * refuse everything; the contract is instead that the result takes exactly one
 * material, which is the only reason to merge in the first place. Inputs are
 * disposed — they exist only to be copied out of.
 */
export const mergeGeometries = (parts: readonly BufferGeometry[]): BufferGeometry => {
  let vertexCount = 0;
  let indexCount = 0;
  for (const part of parts) {
    const position = part.getAttribute('position');
    vertexCount += position.count;
    const index = part.getIndex();
    indexCount += index ? index.count : position.count;
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const indices = new Uint32Array(indexCount);
  let vertexAt = 0;
  let indexAt = 0;

  for (const part of parts) {
    const position = part.getAttribute('position');
    const normal = part.getAttribute('normal');
    const uv = part.getAttribute('uv');
    for (let i = 0; i < position.count; i++) {
      for (let a = 0; a < 3; a++) {
        positions[(vertexAt + i) * 3 + a] = position.getComponent(i, a);
        normals[(vertexAt + i) * 3 + a] = normal ? normal.getComponent(i, a) : 0;
      }
      uvs[(vertexAt + i) * 2] = uv ? uv.getComponent(i, 0) : 0;
      uvs[(vertexAt + i) * 2 + 1] = uv ? uv.getComponent(i, 1) : 0;
    }
    const index = part.getIndex();
    for (let i = 0; i < (index ? index.count : position.count); i++) {
      indices[indexAt + i] = (index ? index.getX(i) : i) + vertexAt;
    }
    indexAt += index ? index.count : position.count;
    vertexAt += position.count;
    part.dispose();
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
};

// ---------------------------------------------------------------------------
// Measurement
// ---------------------------------------------------------------------------

export interface Bounds {
  min: [number, number, number];
  max: [number, number, number];
}

/** Axis-aligned bounds of a geometry's positions, in the MESH frame. */
export const geometryBounds = (geometry: BufferGeometry): Bounds => {
  const p = geometry.getAttribute('position');
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < p.count; i++) {
    for (let a = 0; a < 3; a++) {
      const v = p.getComponent(i, a);
      if (v < min[a]) min[a] = v;
      if (v > max[a]) max[a] = v;
    }
  }
  return { min, max };
};

/** The same bounds expressed back in the car frame: [x forward, y right, z up]. */
export const bodyExtentsCarFrame = (geometry: BufferGeometry): Bounds => {
  const b = geometryBounds(geometry);
  return {
    min: [-b.max[2], b.min[0], b.min[1]],
    max: [-b.min[2], b.max[0], b.max[1]],
  };
};

/** The hitbox corners in the car frame, for comparison against the above. */
export const hitboxExtents = (spec: CarBodySpec): Bounds => ({
  min: [
    spec.hitboxOffset.x - spec.hitbox.length / 2,
    spec.hitboxOffset.y - spec.hitbox.width / 2,
    spec.hitboxOffset.z - spec.hitbox.height / 2,
  ],
  max: [
    spec.hitboxOffset.x + spec.hitbox.length / 2,
    spec.hitboxOffset.y + spec.hitbox.width / 2,
    spec.hitboxOffset.z + spec.hitbox.height / 2,
  ],
});
